/* product-gallery.js — Sneak Freak (AphCom) · <sf-product-gallery>
   One component owns the media order. Slides, thumbs, collage tiles and the zoom viewer all address the same
   media, so the arrows, the thumb strip, a tile tap and a variant change can never disagree about which picture
   is showing. All slides exist in the DOM; the first one is visible without JS.

   Two things used to break navigation and are fixed at the source here:
   1. the viewer took pointer capture on pointerdown, which retargets the following `click` to the viewer — the
      arrow buttons sit inside the viewer, so their own click listener never ran. Capture is now taken only once
      a drag has actually started, so a tap on a control stays a click on that control.
   2. slide and thumb positions were read from template-written data-index attributes. They are now rebased from
      DOM order on connect, and every lookup that has to cross lists (variant change, tile → zoom) goes through
      data-media-id rather than through a second index.

   Touch (request 1.1 "images change on scroll"): a vertical scroll that started on the picture changed the
   picture. The browser takes a vertical pan over (touch-action: pan-y) and fires pointercancel — whose clientX
   is 0 — and the old release handler read that as a long leftward swipe. Now:
   · the gesture is direction-locked on its first real movement: vertical-dominant is dropped at once and the
     page scrolls natively; only a horizontal-dominant drag is ever ours;
   · pointercancel never commits anything (the browser took the gesture: a scroll or a pinch);
   · a swipe commits on distance or on a quick flick, mirrored in RTL, and the picture follows the finger a
     little so the shopper can see the swipe was understood;
   · the thumb strip scrolls itself sideways only — scrollIntoView used to scroll the PAGE back up to the
     thumbs whenever a variant change selected a new picture.

   Colour sync (request 5.2). Only variant → gallery existed: a thumb, an arrow or a swipe onto the other
   colour's photos left the colour name, the selected swatch, the ?variant= URL and the bag's variant id on the
   old colour. Slides now carry data-colour (product-gallery.liquid groups the media by each colour's featured
   media), and:
   · a SHOPPER's move (thumb, arrow, swipe, keyboard, closing the zoom viewer on another photo) onto a slide of
     another colour dispatches `gallery:colour` {sectionId, position, colour, mediaId}; the variant picker
     (product-form.js) selects that colour and re-renders as for a swatch click;
   · a colour picked in the picker moves the gallery at once (on the radio's change, before the section
     request returns) to that colour's first photo;
   · variant:change compares state: when the showing photo already belongs to the variant's colour (the change
     came from the gallery, or only the size changed) the gallery stays where it is. Programmatic show() never
     dispatches, so neither direction can echo into the other. Products without colour groups keep the old
     behaviour exactly (a variant change shows its featured media). */
'use strict';
(function () {
  const theme = window.theme || {};
  if (customElements.get('sf-product-gallery')) return;

  const ZOOM_STEPS = [1, 1.6, 2.4, 3.2];
  const DRAG_START = 8;   /* px before a press becomes a drag (below this a tap is still a click) */
  const SWIPE_MIN = 40;   /* px of travel that commits a swipe to the next/previous media */
  const FLICK_MIN = 18;   /* px — a shorter swipe still commits when it is fast … */
  const FLICK_SPEED = 0.45; /* … px/ms */
  const FOLLOW = 0.35;    /* how much of the finger's travel the picture follows while dragging */
  const TAP_GAP = 300;    /* ms between the taps of a double-tap in the zoom viewer */
  const isRtl = () => (document.documentElement.getAttribute('dir') || document.dir) === 'rtl';

  customElements.define('sf-product-gallery', class extends HTMLElement {
    connectedCallback() {
      /* A custom element upgrades the moment its opening tag is parsed, which can be BEFORE any of its
         children exist. Binding at that point produces exactly the failure reported: every list comes back
         empty, nothing is wired, and no error is thrown anywhere. Wait for the parser in that case. */
      if (!this.querySelector('[data-slide]') && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        return;
      }
      if (this._booted) return;
      this._booted = true;
      this._ctrl = new AbortController();
      const { signal } = this._ctrl;
      this.viewer = this.querySelector('[data-viewer]');
      this.spinner = this.querySelector('[data-spinner]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.tiles = Array.from(this.querySelectorAll('[data-collage-tile]'));
      this.status = this.querySelector('[data-gallery-status]');
      this.thumbStrip = this.querySelector('[data-thumbs]');
      this.zoomBtn = this.querySelector('[data-gallery-zoom]');
      this.rotation = 0;
      this.canZoom = this.dataset.zoom === 'true';
      /* colour groups: the option position, and the colour the shopper has selected (as far as the gallery knows) */
      this.colourPos = this.dataset.colourOption || '';
      this.colour = this.hasAttribute('data-colour') ? this.dataset.colour : null;
      this.grouped = !!this.colourPos && this.querySelector('[data-slide][data-colour]') !== null;

      /* DOM order is the truth. The template emits thumbs 1:1 with slides, so rebase both from position and
         only fall back to the attribute if a future template ever breaks that pairing. */
      this.slides.forEach((s, i) => { s.dataset.index = String(i); });
      if (this.thumbs.length === this.slides.length) this.thumbs.forEach((t, i) => { t.dataset.index = String(i); });
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      /* open on the selected colour even if no slide was marked for it (e.g. a cached page) */
      if (this.grouped && this.colour !== null) {
        const a = this.slides[this.index];
        if (!(a && a.hasAttribute('data-colour') && a.dataset.colour === this.colour)) {
          const i = this._colourStart(this.colour);
          if (i >= 0) this.index = i;
        }
      }

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.show(Number(t.dataset.index), 0, 'user'), { signal }));
      const prev = this.querySelector('[data-gallery-prev]'), next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1, 'user'), { signal });
      if (next) next.addEventListener('click', () => this.step(1, 'user'), { signal });
      /* the round magnifier on the main picture (Figma 1.13) opens the viewer on whatever is showing */
      if (this.zoomBtn) this.zoomBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const slide = this.active;
        if (this.canZoom && this._isImageSlide(slide)) this._openFor(slide.dataset.mediaId, this.zoomBtn);
      }, { signal });

      /* a collage tile opens its own image, never "the first one" */
      this.tiles.forEach((tile) => tile.addEventListener('click', (e) => {
        e.preventDefault();
        if (this._suppressClick) return;
        this._openFor(tile.dataset.mediaId, tile);
      }, { signal }));

      if (this.viewer) {
        this.viewer.setAttribute('tabindex', '0');
        this.viewer.addEventListener('keydown', (e) => this._key(e), { signal });
        this.viewer.addEventListener('pointerdown', (e) => this._down(e), { signal });
        this.viewer.addEventListener('pointermove', (e) => this._move(e), { signal });
        this.viewer.addEventListener('pointerup', (e) => this._up(e), { signal });
        this.viewer.addEventListener('pointercancel', (e) => this._up(e), { signal });
        this.viewer.addEventListener('click', (e) => this._viewerClick(e), { signal });
      }

      /* Render first: the arrows, the thumbs and the active slide must not depend on anything optional
         that follows. The zoom viewer is an enhancement on top of a gallery that already works. */
      this._render();

      try {
        this._setupZoom(signal);
      } catch (err) {
        /* a broken viewer costs the zoom, never the gallery */
        this.canZoom = false;
        this.classList.remove('is-openable');
        if (window.console) console.warn('[sf-product-gallery] zoom viewer disabled:', err);
      }
      this._syncZoomButton();

      this._unsub = theme.pubsub && theme.pubsub.subscribe ? theme.pubsub.subscribe('variant:change', (d) => this._variant(d)) : null;
      /* a colour chosen in this section's picker (swatch, chip or select) moves the pictures immediately */
      if (this.grouped) document.addEventListener('change', (e) => this._pickerChange(e), { signal });
      document.addEventListener('shopify:block:select', (e) => {
        const s = e.target && e.target.closest && e.target.closest('[data-slide]');
        if (s && this.contains(s)) this.show(Number(s.dataset.index));
      }, { signal });

      /* Readable from the page without a debugger: "true" means the component bound to real children,
         "empty" means it connected but found no slides. */
      this.setAttribute('data-gallery-ready', this.slides.length ? 'true' : 'empty');
      if (!this.slides.length && window.console) console.warn('[sf-product-gallery] connected with no [data-slide] children');
    }

    disconnectedCallback() {
      if (!this._ctrl) return;
      this._ctrl.abort();
      if (this._unsub) this._unsub();
      if (this._locked) { if (theme.scrollLock) theme.scrollLock.unlock(this._lockKey); this._locked = false; }
    }

    get active() { return this.slides[this.index]; }

    /* dir (±1) only picks the side the new picture slides in from; omitted, it follows the index change.
       source 'user' marks a shopper's own move: only that may change the colour (programmatic calls — a variant
       change, the editor — never do, which is what keeps gallery and picker from echoing each other). */
    show(i, dir, source) {
      if (!this.slides.length) return;
      const n = this.slides.length;
      const from = this.index;
      this.index = ((i % n) + n) % n;
      this.rotation = 0;
      this._render();
      if (this.index !== from) this._enter(dir || (this.index > from ? 1 : -1));
      if (source === 'user') this._emitColour();
    }

    step(dir, source) { this.show(this.index + dir, dir, source); }

    /* the shopper is now looking at another colour's photo → ask the picker for that colour (once per change) */
    _emitColour() {
      if (!this.grouped) return;
      const slide = this.active;
      const colour = slide && slide.hasAttribute('data-colour') ? slide.dataset.colour : null;
      if (colour === null || colour === this.colour) return;
      this.colour = colour;
      this.dispatchEvent(new CustomEvent('gallery:colour', {
        bubbles: true,
        detail: { sectionId: this.dataset.section, position: Number(this.colourPos), colour, mediaId: slide.dataset.mediaId }
      }));
    }

    /* the first slide of a colour (its featured media opens the group), or -1 */
    _colourStart(colour) {
      return this.slides.findIndex((s) => s.hasAttribute('data-colour') && s.dataset.colour === colour);
    }

    _colourOfVariant(v) {
      const p = Number(this.colourPos);
      if (!v || !p) return null;
      const opts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3];
      const c = opts[p - 1];
      return c === undefined || c === null ? null : String(c);
    }

    /* a colour picked in this section's own picker: show that colour's first photo now, without waiting for the
       section request (the variant:change that follows then finds the gallery already on that colour) */
    _pickerChange(e) {
      const t = e.target;
      if (!t || !t.closest || !t.matches('input[type="radio"], select')) return;
      const fs = t.closest('fieldset[data-option-position]');
      const picker = fs && fs.closest('sf-variant-picker');
      if (!picker || picker.dataset.section !== this.dataset.section || fs.dataset.optionPosition !== String(this.colourPos)) return;
      if (t.type === 'radio' && !t.checked) return;
      this._toColour(t.value);
    }

    _toColour(colour) {
      if (colour === null || colour === undefined) return;
      this.colour = colour;
      const slide = this.active;
      if (slide && slide.hasAttribute('data-colour') && slide.dataset.colour === colour) return;
      const i = this._colourStart(colour);
      if (i >= 0 && i !== this.index) this.show(i);
    }

    /* a short slide-in from the side the shopper is moving towards (mirrored in RTL by the CSS) */
    _enter(dir) {
      const slide = this.active;
      if (!slide || !document.documentElement.classList.contains('motion-ok')) return;
      slide.classList.remove('is-enter-next', 'is-enter-prev');
      void slide.offsetWidth; /* restart the animation when the same slide is entered twice in a row */
      slide.classList.add(dir > 0 ? 'is-enter-next' : 'is-enter-prev');
      slide.addEventListener('animationend', () => slide.classList.remove('is-enter-next', 'is-enter-prev'), { once: true });
    }

    /* index of the slide that shows a given media id; the collage slide answers for its lead image */
    slideIndexOfMedia(id) {
      if (id == null) return -1;
      return this.slides.findIndex((s) => String(s.dataset.mediaId) === String(id));
    }

    _render() {
      this.slides.forEach((s, i) => {
        const on = i === this.index;
        s.classList.toggle('is-active', on);
        s.toggleAttribute('hidden', !on);
      });
      this.thumbs.forEach((t) => {
        const on = Number(t.dataset.index) === this.index;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on) this._centreThumb(t);
      });
      const slide = this.active;
      const spin = !!(slide && slide.hasAttribute('data-spin'));
      this.classList.toggle('is-spinnable', spin);
      this.classList.toggle('is-collage', !!(slide && slide.hasAttribute('data-collage')));
      this.classList.toggle('is-openable', this.canZoom && this._isImageSlide(slide));
      this._syncZoomButton();
      this._transform();
      if (this.status && slide) this.status.textContent = (this.index + 1) + ' / ' + this.slides.length;
      this._rendered = true;
    }

    /* Bring the active thumb into the strip's view by scrolling the STRIP sideways. scrollIntoView would also
       scroll the page vertically to the strip — which is what yanked the page up on a variant change. scrollBy
       with a physical delta behaves the same in LTR and RTL. */
    _centreThumb(t) {
      const strip = this.thumbStrip;
      if (!strip || strip.scrollWidth <= strip.clientWidth + 1) return;
      const sr = strip.getBoundingClientRect(), tr = t.getBoundingClientRect();
      const delta = (tr.left + tr.width / 2) - (sr.left + sr.width / 2);
      if (Math.abs(delta) < 2) return;
      /* the first render positions the strip instantly; later changes glide */
      const smooth = this._rendered && !theme.reducedMotion && document.documentElement.classList.contains('motion-ok');
      if (typeof strip.scrollBy === 'function') strip.scrollBy({ left: delta, behavior: smooth ? 'smooth' : 'auto' });
      else strip.scrollLeft += delta;
    }

    _syncZoomButton() {
      if (!this.zoomBtn) return;
      this.zoomBtn.hidden = !(this.canZoom && this._isImageSlide(this.active));
    }

    _isImageSlide(slide) {
      if (!slide) return false;
      if (slide.hasAttribute('data-collage')) return true;
      const type = slide.dataset.mediaType;
      return !type || type === 'image';
    }

    _transform() {
      const slide = this.active; if (!slide) return;
      const img = slide.querySelector('img, video, model-viewer, iframe');
      if (!img) return;
      img.style.transform = slide.hasAttribute('data-spin') ? 'rotateY(' + this.rotation + 'deg)' : '';
    }

    _key(e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(isRtl() ? -1 : 1, 'user'); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(isRtl() ? 1 : -1, 'user'); }
      else if ((e.key === 'Enter' || e.key === ' ') && e.target === this.viewer && this.canZoom) {
        const slide = this.active;
        if (this._isImageSlide(slide)) { e.preventDefault(); this._openFor(slide.dataset.mediaId, this.viewer); }
      }
    }

    /* ——— press / drag on the main viewer ———
       A press on an arrow or the zoom button is still allowed to become a swipe: no capture is taken until the
       drag is real, so a tap on those controls stays their own click. */
    _down(e) {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      if (e.target.closest('video, model-viewer, iframe')) return;
      /* a second finger means a pinch: hand the gesture to the browser (page zoom) and forget the drag */
      if (!e.isPrimary) { this._abandon(); return; }
      this._drag = { x: e.clientX, y: e.clientY, rot: this.rotation, axis: null, moved: false, id: e.pointerId, dx: 0, px: e.clientX, pt: e.timeStamp, vx: 0 };
      /* no setPointerCapture here — see the header note; it is taken in _move once a drag is real */
    }

    _move(e) {
      const d = this._drag; if (!d || d.id !== e.pointerId || d.axis === 'y') return;
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      if (!d.axis) {
        if (Math.abs(dx) <= DRAG_START && Math.abs(dy) <= DRAG_START) return;
        /* Direction lock, decided once by the first real movement. Vertical-dominant is the page's: no capture,
           no follow, no swipe — the browser scrolls (touch) and the release is ignored. */
        if (Math.abs(dy) >= Math.abs(dx)) { d.axis = 'y'; return; }
        d.axis = 'x';
        d.moved = true;
        if (this.viewer.setPointerCapture) { try { this.viewer.setPointerCapture(e.pointerId); } catch (err) { /* pointer already gone */ } }
        this.classList.add('is-grabbing');
      }
      /* velocity from the latest sample, for the flick */
      const dt = e.timeStamp - d.pt;
      if (dt > 0) d.vx = (e.clientX - d.px) / dt;
      d.px = e.clientX; d.pt = e.timeStamp; d.dx = dx;
      const slide = this.active;
      if (slide && slide.hasAttribute('data-spin')) { this.rotation = d.rot + dx * 0.7; this._transform(); return; }
      this._follow(dx);
    }

    _up(e) {
      const d = this._drag; if (!d || d.id !== e.pointerId) return;
      this._drag = null;
      this.classList.remove('is-grabbing');
      /* a drag in either direction must not also fire the tile/viewer click that follows it (a mouse dragged
         vertically still clicks on release, which used to open the zoom viewer) */
      if (d.moved || d.axis === 'y') {
        this._suppressClick = true;
        setTimeout(() => { this._suppressClick = false; }, 0);
      }
      if (!d.moved) return;
      this._follow(0);
      /* pointercancel means the browser took the gesture (a scroll, a pinch, the OS). Its coordinates are 0,0,
         so it must never be read as a swipe — that was the "image changes when I scroll" bug. */
      if (e.type === 'pointercancel') return;
      const slide = this.active;
      if (slide && slide.hasAttribute('data-spin')) return;
      const dx = e.clientX - d.x;
      const recent = e.timeStamp - d.pt < 80;
      const flick = recent && Math.abs(dx) > FLICK_MIN && Math.abs(d.vx) > FLICK_SPEED && Math.sign(d.vx) === Math.sign(dx);
      if (Math.abs(dx) <= SWIPE_MIN && !flick) return;
      /* finger moving left reveals what comes next in LTR; in RTL the next picture comes from the left */
      const forward = isRtl() ? dx > 0 : dx < 0;
      this.step(forward ? 1 : -1, 'user');
    }

    _abandon() {
      if (!this._drag) return;
      const moved = this._drag.moved;
      this._drag = null;
      this.classList.remove('is-grabbing');
      if (moved) this._follow(0);
    }

    /* the picture follows a fraction of the finger, so a swipe is visibly understood before it commits */
    _follow(dx) {
      const slide = this.active; if (!slide) return;
      slide.style.transform = dx ? 'translate3d(' + (dx * FOLLOW).toFixed(1) + 'px,0,0)' : '';
      slide.style.opacity = dx ? String(Math.max(0.55, 1 - Math.abs(dx) / 600)) : '';
    }

    _viewerClick(e) {
      if (this._suppressClick || !this.canZoom) return;
      if (e.target.closest('[data-gallery-prev], [data-gallery-next], [data-gallery-zoom], [data-collage-tile], video, model-viewer, iframe')) return;
      const slide = this.active;
      if (!this._isImageSlide(slide)) return;
      this._openFor(slide.dataset.mediaId, this.viewer);
    }

    /* ——— zoom viewer ——— */
    _setupZoom(signal) {
      this.dialog = this.querySelector('[data-zoom-dialog]');
      if (!this.dialog || !this.canZoom) { this.canZoom = false; return; }
      this.zoomSlides = Array.from(this.dialog.querySelectorAll('[data-zoom-slide]'));
      if (!this.zoomSlides.length) { this.canZoom = false; return; }
      this.zoomStage = this.dialog.querySelector('[data-zoom-stage]');
      this.zoomCount = this.dialog.querySelector('[data-zoom-count]');
      this.zoomIndex = 0; this.scale = 1; this.pan = { x: 0, y: 0 };
      this._lockKey = 'gallery-zoom:' + (this.dataset.section || 'main');

      const on = (sel, ev, fn) => { const el = this.dialog.querySelector(sel); if (el) el.addEventListener(ev, fn, { signal }); };
      on('[data-zoom-close]', 'click', () => this.closeZoom());
      on('[data-zoom-prev]', 'click', () => this.zoomStep(-1));
      on('[data-zoom-next]', 'click', () => this.zoomStep(1));
      on('[data-zoom-in]', 'click', () => this._scaleBy(1));
      on('[data-zoom-out]', 'click', () => this._scaleBy(-1));

      /* <dialog> gives the focus scope and focus return. Escape is handled here as well as through the
         platform's cancel event: a close request does not always reach the dialog when another layer of the
         page is listening for Escape, and the viewer must close on the first press either way. */
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.closeZoom(); }, { signal });
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.closeZoom(); }, { signal });
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.zoomStep(document.dir === 'rtl' ? -1 : 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); this.zoomStep(document.dir === 'rtl' ? 1 : -1); }
        else if (e.key === '+' || e.key === '=') { e.preventDefault(); this._scaleBy(1); }
        else if (e.key === '-') { e.preventDefault(); this._scaleBy(-1); }
        else if (e.key === 'Escape') { e.preventDefault(); this.closeZoom(); }
      }, { signal });

      if (this.zoomStage) {
        this._pointers = new Map();
        /* Gestures are read on the whole frame, not only the stage: on a portrait phone the arrow row (and an
           iPhone's home-bar inset) is frame padding under the photo, right in the thumb zone. A press that starts
           on the toolbar or an arrow stays theirs. The zoom and pan maths still measure the stage. */
        const pad = this.dialog.querySelector('.pdp-zoom__frame') || this.zoomStage;
        const own = (e) => !(e.target.closest && e.target.closest('.pdp-zoom__bar, .pdp-zoom__nav'));
        pad.addEventListener('pointerdown', (e) => { if (own(e)) this._zDown(e); }, { signal });
        pad.addEventListener('pointermove', (e) => this._zMove(e), { signal });
        pad.addEventListener('pointerup', (e) => this._zUp(e), { signal });
        pad.addEventListener('pointercancel', (e) => this._zUp(e), { signal });
        pad.addEventListener('dblclick', (e) => { if (own(e)) this._zDouble(e); }, { signal });
        pad.addEventListener('wheel', (e) => { if (own(e)) this._zWheel(e); }, { signal, passive: false });
        /* the browser's own image drag would take over a mouse pan after the first move (dragstart, then
           pointercancel); the CSS -webkit-user-drag covers WebKit/Blink, this covers Firefox */
        pad.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
      }
    }

    zoomIndexOfMedia(id) {
      if (id == null || !this.zoomSlides) return -1;
      return this.zoomSlides.findIndex((s) => String(s.dataset.mediaId) === String(id));
    }

    /* a tile, a slide or the keyboard asking for "this media" — open the viewer on it, or at least select it */
    _openFor(mediaId, opener) {
      if (!this.canZoom) { const i = this.slideIndexOfMedia(mediaId); if (i >= 0) this.show(i, 0, 'user'); return; }
      const z = this.zoomIndexOfMedia(mediaId);
      this.openZoom(z >= 0 ? z : 0, opener);
    }

    openZoom(i, opener) {
      if (!this.canZoom || !this.dialog) return;
      this._opener = opener || document.activeElement;
      this._setZoomIndex(i);
      this._openedAt = this.zoomIndex;
      if (typeof this.dialog.showModal === 'function' && !this.dialog.open) this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      if (theme.scrollLock && !this._locked) { theme.scrollLock.lock(this._lockKey); this._locked = true; }
      const close = this.dialog.querySelector('[data-zoom-close]');
      if (close) close.focus({ preventScroll: true });
    }

    closeZoom() {
      if (!this.dialog) return;
      /* Moving inside the viewer moves the gallery with it; opening a tile and closing it again leaves the
         collage exactly as it was, so the button that opened the viewer is still there to take focus back. */
      if (this.zoomIndex !== this._openedAt) {
        const media = this.zoomSlides[this.zoomIndex] && this.zoomSlides[this.zoomIndex].dataset.mediaId;
        const s = this.slideIndexOfMedia(media);
        if (s >= 0 && s !== this.index) this.show(s, 0, 'user');
      }
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
      if (theme.scrollLock && this._locked) { theme.scrollLock.unlock(this._lockKey); this._locked = false; }
      this.scale = 1; this.pan = { x: 0, y: 0 };
      /* the opener can have been hidden by the slide change above — then the viewer takes focus instead */
      const back = this._opener;
      this._opener = null;
      const target = back && back.isConnected && back.offsetParent !== null ? back : this.viewer;
      if (target && target.focus) target.focus({ preventScroll: true });
    }

    zoomStep(dir) { this._setZoomIndex(this.zoomIndex + dir); }

    _setZoomIndex(i) {
      const n = this.zoomSlides.length;
      this.zoomIndex = ((i % n) + n) % n;
      this.scale = 1; this.pan = { x: 0, y: 0 };
      this.zoomSlides.forEach((s, k) => {
        const on = k === this.zoomIndex;
        s.toggleAttribute('hidden', !on);
        s.classList.toggle('is-active', on);
        const img = s.querySelector('img');
        if (img && on) img.loading = 'eager';
      });
      /* keep the immediate neighbours decoded so next/prev does not flash an empty frame */
      [this.zoomIndex - 1, this.zoomIndex + 1].forEach((k) => {
        const s = this.zoomSlides[((k % n) + n) % n];
        const img = s && s.querySelector('img');
        if (img) img.loading = 'eager';
      });
      if (this.zoomCount) this.zoomCount.textContent = (this.zoomIndex + 1) + ' / ' + n;
      this._pinch = null;
      this._lastTap = null;
      if (this._pointers) this._pointers.clear();
      this._zTransform();
      /* naturalWidth is 0 until the frame decodes; re-clamp once it is known so the first pan is correct */
      const cur = this.zoomSlides[this.zoomIndex] && this.zoomSlides[this.zoomIndex].querySelector('img');
      if (cur && !cur.complete) cur.addEventListener('load', () => { this._clampPan(); this._zTransform(); }, { once: true });
    }

    /* The rendered size of the PHOTO at scale 1. The <img> box fills the stage and object-fit:contain
       letterboxes the picture inside it, so the box and the picture are different rectangles — for a portrait
       shot in a landscape stage, very different. Every limit below is derived from the picture. */
    _baseImageBox() {
      const img = this.zoomSlides[this.zoomIndex] && this.zoomSlides[this.zoomIndex].querySelector('img');
      if (!img) return null;
      const nw = img.naturalWidth || 0, nh = img.naturalHeight || 0;
      const bw = img.clientWidth, bh = img.clientHeight;
      if (!nw || !nh || !bw || !bh) return null;
      const fit = Math.min(bw / nw, bh / nh);
      return { w: nw * fit, h: nh * fit };
    }

    /* How far the scaled picture may travel: only as far as it actually overflows the stage. At scale 1 this
       is {0,0} on both axes, so an unzoomed image cannot be dragged around. */
    _panLimit() {
      const box = this._baseImageBox();
      const r = this.zoomStage.getBoundingClientRect();
      if (!box) return { x: 0, y: 0 };
      return {
        x: Math.max(0, (box.w * this.scale - r.width) / 2),
        y: Math.max(0, (box.h * this.scale - r.height) / 2)
      };
    }

    _clampPan() {
      const lim = this._panLimit();
      this.pan = {
        x: Math.max(-lim.x, Math.min(lim.x, this.pan.x)),
        y: Math.max(-lim.y, Math.min(lim.y, this.pan.y))
      };
    }

    /* Zoom about a point rather than the centre, so the thing under the cursor or between the fingers stays
       under it. Panning is re-clamped on every change, which is what stops a zoom-out leaving the picture
       stranded off to one side. */
    _zoomTo(next, cx, cy) {
      const r = this.zoomStage.getBoundingClientRect();
      const lo = ZOOM_STEPS[0], hi = ZOOM_STEPS[ZOOM_STEPS.length - 1];
      const to = Math.min(hi, Math.max(lo, next));
      const ox = (cx == null ? r.width / 2 : cx - r.left) - r.width / 2;
      const oy = (cy == null ? r.height / 2 : cy - r.top) - r.height / 2;
      const k = to / (this.scale || 1);
      this.pan = { x: (this.pan.x - ox) * k + ox, y: (this.pan.y - oy) * k + oy };
      this.scale = to;
      if (to <= lo) this.pan = { x: 0, y: 0 };
      this._clampPan();
      this._zTransform();
    }

    _scaleBy(dir) {
      const cur = this.scale;
      let to;
      if (dir > 0) to = ZOOM_STEPS.find((s) => s > cur + 0.001) || ZOOM_STEPS[ZOOM_STEPS.length - 1];
      else { const below = ZOOM_STEPS.filter((s) => s < cur - 0.001); to = below.length ? below[below.length - 1] : ZOOM_STEPS[0]; }
      this._zoomTo(to);
    }

    _zWheel(e) {
      if (!this.dialog || !this.dialog.open) return;
      e.preventDefault();
      const factor = Math.exp(-e.deltaY / 420);
      this._zoomTo(this.scale * factor, e.clientX, e.clientY);
    }

    _zTransform() {
      if (!this.dialog) return;
      this.dialog.classList.toggle('is-zoomed', this.scale > 1);
      const img = this.zoomSlides[this.zoomIndex] && this.zoomSlides[this.zoomIndex].querySelector('img');
      if (img) img.style.transform = 'translate(' + this.pan.x + 'px,' + this.pan.y + 'px) scale(' + this.scale + ')';
      const out = this.dialog.querySelector('[data-zoom-out]'), zin = this.dialog.querySelector('[data-zoom-in]');
      const had = document.activeElement;
      if (out) out.disabled = this.scale <= ZOOM_STEPS[0];
      if (zin) zin.disabled = this.scale >= ZOOM_STEPS[ZOOM_STEPS.length - 1];
      /* a zoom button that has just disabled itself would drop focus to <body>, outside the modal: hand it to the
         other one */
      if (had && had.disabled && this.dialog.open) {
        const alt = had === out ? zin : out;
        if (alt && !alt.disabled) alt.focus({ preventScroll: true });
      }
    }

    _zDown(e) {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      if (this._pointers) this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this._pointers && this._pointers.size === 2) {
        /* a second finger turns the gesture into a pinch; the drag in progress is abandoned */
        this._zdrag = null;
        const [a, b] = Array.from(this._pointers.values());
        this._pinch = {
          dist: Math.hypot(b.x - a.x, b.y - a.y) || 1,
          scale: this.scale,
          cx: (a.x + b.x) / 2,
          cy: (a.y + b.y) / 2
        };
        return;
      }
      this._zdrag = { x: e.clientX, y: e.clientY, px: this.pan.x, py: this.pan.y, moved: false, id: e.pointerId };
    }

    _zMove(e) {
      if (this._pointers && this._pointers.has(e.pointerId)) this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this._pinch && this._pointers && this._pointers.size >= 2) {
        const [a, b] = Array.from(this._pointers.values());
        const dist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
        this._zoomTo(this._pinch.scale * (dist / this._pinch.dist), this._pinch.cx, this._pinch.cy);
        return;
      }
      const d = this._zdrag; if (!d || d.id !== e.pointerId) return;
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      if (!d.moved && (Math.abs(dx) > DRAG_START || Math.abs(dy) > DRAG_START)) {
        d.moved = true;
        if (this.zoomStage.setPointerCapture) { try { this.zoomStage.setPointerCapture(e.pointerId); } catch (err) { /* pointer already gone */ } }
        this.dialog.classList.add('is-grabbing');
      }
      if (!d.moved || this.scale <= 1) return;
      const lim = this._panLimit();
      this.pan = { x: Math.max(-lim.x, Math.min(lim.x, d.px + dx)), y: Math.max(-lim.y, Math.min(lim.y, d.py + dy)) };
      this._zTransform();
    }

    _zUp(e) {
      if (this._pointers) this._pointers.delete(e.pointerId);
      if (this._pinch && (!this._pointers || this._pointers.size < 2)) { this._pinch = null; this._zdrag = null; this._lastTap = null; return; }
      const d = this._zdrag; if (!d || d.id !== e.pointerId) return;
      this._zdrag = null;
      this.dialog.classList.remove('is-grabbing');
      if (e.type === 'pointercancel') { this._lastTap = null; return; }
      if (!d.moved) {
        /* Double-tap to zoom on touch. Not every mobile browser turns two taps into a dblclick, so the taps are
           paired here; the dblclick that may still follow is ignored for a moment. */
        if (e.pointerType !== 'mouse') {
          const now = performance.now(), last = this._lastTap;
          if (last && now - last.t < TAP_GAP && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 30) {
            this._lastTap = null;
            this._tapZoomAt = now;
            this._toggleZoomAt(e.clientX, e.clientY);
          } else {
            this._lastTap = { t: now, x: e.clientX, y: e.clientY };
          }
        }
        return;
      }
      this._lastTap = null;
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      if (this.scale === 1 && this.zoomSlides.length > 1 && Math.abs(dx) > SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
        const forward = isRtl() ? dx > 0 : dx < 0;
        this.zoomStep(forward ? 1 : -1);
      }
    }

    _zDouble(e) {
      if (this._tapZoomAt && performance.now() - this._tapZoomAt < 500) return; /* the touch pair already did it */
      this._toggleZoomAt(e.clientX, e.clientY);
    }

    _toggleZoomAt(x, y) {
      if (this.scale > ZOOM_STEPS[0]) { this._zoomTo(ZOOM_STEPS[0]); return; }
      this._zoomTo(ZOOM_STEPS[2], x, y);
    }

    _variant(d) {
      if (!d || d.sectionId !== this.dataset.section) return;
      const v = d.variant; const mediaId = v && v.featured_media && v.featured_media.id;
      if (this.grouped) {
        const colour = this._colourOfVariant(v);
        if (colour !== null) {
          this.colour = colour;
          /* compare state: already on this colour (the change came from the gallery, or only the size changed) */
          const slide = this.active;
          if (slide && slide.hasAttribute('data-colour') && slide.dataset.colour === colour) return;
          let i = this.slideIndexOfMedia(mediaId);
          if (i < 0) i = this._colourStart(colour);
          if (i >= 0 && i !== this.index) this.show(i);
          if (this.dialog && this.dialog.open && i >= 0) {
            const z = this.zoomIndexOfMedia(this.slides[i].dataset.mediaId);
            if (z >= 0) this._setZoomIndex(z);
          }
          return;
        }
      }
      if (!mediaId) return;
      const i = this.slideIndexOfMedia(mediaId);
      if (i >= 0) this.show(i);
      if (this.dialog && this.dialog.open) {
        const z = this.zoomIndexOfMedia(mediaId);
        if (z >= 0) this._setZoomIndex(z);
      }
    }
  });
})();
